import React from 'react';
import { Col, Form, Row } from 'antd';
import CustomInput from '@/components/CustomInput';
import CustomTitle from '@/components/CustomTitle';
import CommonFooter from '@/components/CommonFooter';
import type { CredentialData } from '@/apis/customerCredentials';
import { secretLabel } from '../credentialFormat';
import '../../Sensors/sensors.css';
import styles from './RotateCredentialForm.module.css';

const inputProps = {
  bgColor: 'var(--light-gray)',
  textColor: 'var(--text-primary)',
  height: '36px',
  borderRadius: '8px',
  border: 'none',
} as const;

interface RotateCredentialFormProps {
  credential: CredentialData;
  saving: boolean;
  onSave: (secret: string) => void;
  onCancel: () => void;
}

/**
 * The "Rotate value" drawer (PRD 4.1 Rotate, 5.7): the name for context, and a
 * single new-secret field. The old value is never shown, and not kept once
 * replaced.
 */
const RotateCredentialForm: React.FC<RotateCredentialFormProps> = ({
  credential,
  saving,
  onSave,
  onCancel,
}) => {
  const [form] = Form.useForm<{ secret: string }>();
  const isOAuth = credential.auth_type === 'OAUTH_CLIENT_CREDENTIALS';
  const label = secretLabel(credential.auth_type);

  return (
    <Form
      form={form}
      onFinish={(values) => onSave(values.secret)}
      layout='vertical'
      preserve={false}
      className='sensor-credential-form'
    >
      <div className='custom-scrollbar sensor-credential-scroll'>
        <Row gutter={[16, 14]}>
          <Col span={24}>
            <CustomTitle
              text='Rotate value'
              fontSize={24}
              fontWeight='700'
              color='var(--text-primary)'
              marginBottom={0}
            />
          </Col>
          <Col span={24}>
            <Form.Item label='Name'>
              <CustomInput
                disabled
                value={credential.name}
                aria-label='Name (read-only)'
                {...inputProps}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label={isOAuth ? 'New client secret' : 'New API key'}
              name='secret'
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: `Enter the new ${label}`,
                },
              ]}
              extra={
                <span className={styles.helperText}>
                  Automations using this credential switch to new value on next
                  run.
                </span>
              }
            >
              <CustomInput
                type='password'
                autoComplete='new-password'
                placeholder={`Paste the new ${label}`}
                {...inputProps}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <div className='sensor-drawer-footer'>
        <CommonFooter
          text={saving ? 'Saving...' : 'Save'}
          onSave={() => form.submit()}
          handleCancel={onCancel}
        />
      </div>
    </Form>
  );
};

export default RotateCredentialForm;
